import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import styles from './styles.module.scss';

import { apiSaveMailTemplate, apiPreviewMail } from 'api/mailTemplate';

import { HTTP_OK, HTTP_CONFLICT, HTTP_UNPROCESSABLE_ENTITY } from 'constants/StatusCode.js';
import { MAX_LENGTH_255 } from 'constants/index.js';
import { COLOR_CHECKBOX_CUSTOM } from 'constants/ColorVariable';
import { STATUS_CANDIDATE } from 'constants/ManagerCandidate';
import {
  USE_GET_LIST_MAIL_TEMPLATE,
  TRIGGER_TYPE,
  SEND_MODE,
  MAIL_ACTIVE,
  MAIL_INACTIVE,
  LIST_TRIGGER_STATUS,
  MAX_DELAY_MINUTES,
} from 'constants/MailTemplate';

import ModalComponent from 'components/Modal';
import ButtonComponent from 'components/Buttons';
import CustomInputYup from 'components/InputYupAndMui/CustomInputYup';
import { CssTextField } from 'components/InputYupAndMui/constant';
import MailEditor from 'components/MailEditor';
import MailPreviewModal from 'components/MailEditor/MailPreviewModal';

import { showAlert } from 'helpers/modal';
import { getApplyStatusName } from 'helpers/mailTemplate';

const DELAY_UNIT = {
  MINUTE: 1,
  HOUR: 60,
  DAY: 1440,
};

// Trạng thái do cron tự chuyển, TA có 7 ngày để quay lại (AFB-757) -> nên gửi trễ
const LIST_STATUS_SUGGEST_DELAY = [STATUS_CANDIDATE.CANCEL_CV, STATUS_CANDIDATE.LEADER_REFUSE_CANDIDATE];

const EMAIL_REGEX = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

const isEmailList = (value) =>
  !value ||
  value
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean)
    .every((email) => EMAIL_REGEX.test(email));

const emptyToUndefined = (value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value);

const getDelayForm = (minutes) => {
  const value = Number(minutes) || 0;
  if (value && value % DELAY_UNIT.DAY === 0) return { delay_value: value / DELAY_UNIT.DAY, delay_unit: DELAY_UNIT.DAY };
  if (value && value % DELAY_UNIT.HOUR === 0) return { delay_value: value / DELAY_UNIT.HOUR, delay_unit: DELAY_UNIT.HOUR };
  return { delay_value: value, delay_unit: DELAY_UNIT.MINUTE };
};

const getDefaultValues = (formAdd) => ({
  name: formAdd?.name || '',
  subject: formAdd?.subject || '',
  body: formAdd?.body || '',
  trigger_type: formAdd?.trigger_type || TRIGGER_TYPE.APPLY_STATUS,
  trigger_status: formAdd?.trigger_status || '',
  send_mode: formAdd?.send_mode || SEND_MODE.CONFIRM,
  ...getDelayForm(formAdd?.delay_minutes),
  cc: formAdd?.cc || '',
  bcc: formAdd?.bcc || '',
  is_cc_hr: !!formAdd?.is_cc_hr,
  is_active: formAdd ? formAdd.is_active === MAIL_ACTIVE : true,
  preview_apply_id: '',
});

const CreateOrUpdateMailTemplate = ({ isShowModal, setShowModal, formAdd, onSuccess }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState(null);

  const {
    handleSubmit,
    control,
    register,
    setError,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        name: Yup.string().trim().required(t('managerMailTemplate.nameRequired')),
        subject: Yup.string().trim().required(t('managerMailTemplate.subjectRequired')),
        body: Yup.string().required(t('managerMailTemplate.bodyRequired')),
        trigger_status: Yup.mixed().when('trigger_type', {
          is: TRIGGER_TYPE.APPLY_STATUS,
          then: Yup.number()
            .transform(emptyToUndefined)
            .required(t('managerMailTemplate.triggerStatusRequired')),
          otherwise: Yup.mixed().nullable(),
        }),
        delay_value: Yup.mixed().when('send_mode', {
          is: SEND_MODE.AUTO,
          then: Yup.number()
            .transform(emptyToUndefined)
            .typeError(t('managerMailTemplate.delayInvalid'))
            .required(t('managerMailTemplate.delayInvalid'))
            .integer(t('managerMailTemplate.delayInvalid'))
            .min(0, t('managerMailTemplate.delayInvalid')),
          otherwise: Yup.mixed().nullable(),
        }),
        cc: Yup.string().nullable().test('emails', t('managerMailTemplate.emailListInvalid'), isEmailList),
        bcc: Yup.string().nullable().test('emails', t('managerMailTemplate.emailListInvalid'), isEmailList),
      }),
    ),
    defaultValues: getDefaultValues(formAdd),
  });

  const triggerType = useWatch({ control, name: 'trigger_type' });
  const triggerStatus = useWatch({ control, name: 'trigger_status' });
  const sendMode = useWatch({ control, name: 'send_mode' });
  const isManualTrigger = triggerType === TRIGGER_TYPE.MANUAL;
  // BE ép mẫu gửi tay về MANUAL, hiển thị đúng như vậy
  const currentSendMode = isManualTrigger ? SEND_MODE.MANUAL : sendMode;

  const setErrorsFromResponse = (messages) => {
    Object.keys(messages || {}).forEach((key) => {
      const field = key === 'delay_minutes' ? 'delay_value' : key;
      setError(field, { message: messages[key]?.[0] });
    });
  };

  const { mutate: submit, isLoading: isLoadingSubmit } = useMutation(
    (values) => {
      const delayMinutes = currentSendMode === SEND_MODE.AUTO ? values.delay_value * values.delay_unit : 0;
      return apiSaveMailTemplate({
        id: formAdd?.id,
        name: values.name.trim(),
        subject: values.subject.trim(),
        body: values.body,
        trigger_type: values.trigger_type,
        trigger_status: values.trigger_type === TRIGGER_TYPE.APPLY_STATUS ? values.trigger_status : null,
        send_mode: currentSendMode,
        delay_minutes: delayMinutes,
        cc: values.cc,
        bcc: values.bcc,
        is_cc_hr: values.is_cc_hr ? 1 : 0,
        is_active: values.is_active ? MAIL_ACTIVE : MAIL_INACTIVE,
      });
    },
    {
      onSuccess: (response) => {
        const status = response?.data?.status;
        if (status === HTTP_OK) {
          queryClient.invalidateQueries(USE_GET_LIST_MAIL_TEMPLATE);
          onSuccess(formAdd?.id ? t('managerMailTemplate.editSuccess') : t('managerMailTemplate.addSuccess'));
          setShowModal(false);
        } else if (status === HTTP_CONFLICT) {
          setError('trigger_status', { message: response?.data?.message });
        } else if (status === HTTP_UNPROCESSABLE_ENTITY) {
          setErrorsFromResponse(response?.data?.messages);
        } else {
          showAlert(response?.data?.message || t('messages.systemError'));
        }
      },
      onError: () => {
        showAlert(t('messages.systemError'));
      },
    },
  );

  const onSubmit = (values) => {
    if (currentSendMode === SEND_MODE.AUTO && values.delay_value * values.delay_unit > MAX_DELAY_MINUTES) {
      setError('delay_value', { message: t('managerMailTemplate.delayTooLong') });
      return;
    }
    submit(values);
  };

  const { mutate: previewMail, isLoading: isLoadingPreview } = useMutation(
    () => {
      const values = getValues();
      return apiPreviewMail({
        apply_id: values.preview_apply_id,
        template_id: formAdd?.id,
        subject: values.subject,
        body: values.body,
      });
    },
    {
      onSuccess: (response) => {
        if (response?.data?.status === HTTP_OK && response?.data?.data) {
          setPreview(response.data.data);
        } else if (response?.data?.status === HTTP_UNPROCESSABLE_ENTITY) {
          const messages = response?.data?.messages || {};
          setError('preview_apply_id', {
            message: (messages.apply_id || messages.template_id || [t('managerMailTemplate.previewFail')])[0],
          });
        } else {
          setError('preview_apply_id', { message: t('managerMailTemplate.applyNotFound') });
        }
      },
      onError: () => {
        showAlert(t('messages.systemError'));
      },
    },
  );

  const handlePreview = () => {
    const { preview_apply_id: applyId, body } = getValues();
    if (!applyId || !/^\d+$/.test(String(applyId).trim())) {
      setError('preview_apply_id', { message: t('managerMailTemplate.applyIdRequired') });
      return;
    }
    if (!body) {
      setError('body', { message: t('managerMailTemplate.bodyRequired') });
      return;
    }
    previewMail();
  };

  const renderSelect = (name, label, options, { disabled, required } = {}) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <CssTextField
          {...field}
          select
          fullWidth
          label={label}
          required={required}
          disabled={disabled}
          error={!!errors[name]}
          helperText={errors[name]?.message}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </CssTextField>
      )}
    />
  );

  const renderCheckbox = (name, label) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          label={label}
          control={
            <Checkbox
              checked={!!field.value}
              onChange={(event) => field.onChange(event.target.checked)}
              sx={{ '&.Mui-checked': { color: COLOR_CHECKBOX_CUSTOM } }}
            />
          }
        />
      )}
    />
  );

  return (
    <>
      <ModalComponent
        toggle={() => setShowModal(false)}
        isShowModal={isShowModal}
        title={formAdd?.id ? t('managerMailTemplate.editTitle') : t('managerMailTemplate.addTitle')}
        classNameAdd={styles.modalMailTemplate}
      >
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomInputYup
                label={t('managerMailTemplate.name')}
                name="name"
                control={control}
                register={register}
                errors={errors}
                useWatch={useWatch}
                required
                maxLength={MAX_LENGTH_255}
              />
            </Grid>
            <Grid item xs={4}>
              {renderSelect(
                'trigger_type',
                t('managerMailTemplate.trigger'),
                [
                  { value: TRIGGER_TYPE.APPLY_STATUS, label: t('managerMailTemplate.triggerTypeApplyStatus') },
                  { value: TRIGGER_TYPE.INTERVIEW_UPDATED, label: t('managerMailTemplate.triggerInterviewUpdated') },
                  { value: TRIGGER_TYPE.MANUAL, label: t('managerMailTemplate.triggerManual') },
                ],
                { required: true },
              )}
            </Grid>
            <Grid item xs={4}>
              {triggerType === TRIGGER_TYPE.APPLY_STATUS &&
                renderSelect(
                  'trigger_status',
                  t('managerMailTemplate.triggerStatus'),
                  LIST_TRIGGER_STATUS.map((status) => ({ value: status, label: getApplyStatusName(status) })),
                  { required: true },
                )}
            </Grid>
            <Grid item xs={4}>
              {!isManualTrigger &&
                renderSelect(
                  'send_mode',
                  t('managerMailTemplate.sendMode'),
                  [
                    { value: SEND_MODE.CONFIRM, label: t('managerMailTemplate.sendModeConfirm') },
                    { value: SEND_MODE.AUTO, label: t('managerMailTemplate.sendModeAuto') },
                    { value: SEND_MODE.MANUAL, label: t('managerMailTemplate.sendModeManual') },
                  ],
                  { required: true },
                )}
            </Grid>
            <Grid item xs={12} className={styles.hintSendMode}>
              {t(
                currentSendMode === SEND_MODE.AUTO
                  ? 'managerMailTemplate.hintAuto'
                  : currentSendMode === SEND_MODE.CONFIRM
                  ? 'managerMailTemplate.hintConfirm'
                  : 'managerMailTemplate.hintManual',
              )}
            </Grid>
            {currentSendMode === SEND_MODE.AUTO && (
              <>
                <Grid item xs={4}>
                  <Controller
                    name="delay_value"
                    control={control}
                    render={({ field }) => (
                      <CssTextField
                        {...field}
                        type="number"
                        fullWidth
                        label={t('managerMailTemplate.delay')}
                        inputProps={{ min: 0 }}
                        error={!!errors.delay_value}
                        helperText={errors.delay_value?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  {renderSelect('delay_unit', t('managerMailTemplate.delayUnit'), [
                    { value: DELAY_UNIT.MINUTE, label: t('managerMailTemplate.unitMinute') },
                    { value: DELAY_UNIT.HOUR, label: t('managerMailTemplate.unitHour') },
                    { value: DELAY_UNIT.DAY, label: t('managerMailTemplate.unitDay') },
                  ])}
                </Grid>
                <Grid item xs={4} className={styles.hintSendMode}>
                  {LIST_STATUS_SUGGEST_DELAY.includes(triggerStatus) && t('managerMailTemplate.hintDelayReject')}
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <CustomInputYup
                label={t('managerMailTemplate.subject')}
                name="subject"
                control={control}
                register={register}
                errors={errors}
                useWatch={useWatch}
                required
                maxLength={MAX_LENGTH_255}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="body"
                control={control}
                render={({ field }) => (
                  <MailEditor value={field.value} onChange={field.onChange} error={errors.body?.message} />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomInputYup
                label={t('managerMailTemplate.cc')}
                name="cc"
                control={control}
                register={register}
                errors={errors}
                useWatch={useWatch}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomInputYup
                label={t('managerMailTemplate.bcc')}
                name="bcc"
                control={control}
                register={register}
                errors={errors}
                useWatch={useWatch}
              />
            </Grid>
            <Grid item xs={6}>
              {renderCheckbox('is_cc_hr', t('managerMailTemplate.isCcHr'))}
            </Grid>
            <Grid item xs={6}>
              {renderCheckbox('is_active', t('managerMailTemplate.active'))}
            </Grid>
            <Grid item xs={12} className={styles.previewBox}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={8}>
                  <CustomInputYup
                    label={t('managerMailTemplate.previewApplyId')}
                    name="preview_apply_id"
                    control={control}
                    register={register}
                    errors={errors}
                    useWatch={useWatch}
                    invalidPattern={/\D/g}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ButtonComponent
                    text={t('candidateMail.preview')}
                    addClass="btn-bg-yellow2 w-100"
                    handleClick={handlePreview}
                    isLoading={isLoadingPreview}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={2} className="btn-submit mt-3">
            <Grid item xs={6}>
              <ButtonComponent
                text={t('common.cancel')}
                addClass="btn-bg-yellow2 mr-3 w-100"
                handleClick={() => setShowModal(false)}
              />
            </Grid>
            <Grid item xs={6}>
              <ButtonComponent
                text={formAdd?.id ? t('common.save') : t('common.add')}
                addClass="btn-bg-yellow1 w-100"
                hasDisabled={false}
                type="submit"
                handleClick={handleSubmit(onSubmit)}
                isLoading={isLoadingSubmit}
              />
            </Grid>
          </Grid>
        </form>
      </ModalComponent>
      {!!preview && <MailPreviewModal isShowModal={!!preview} setShowModal={() => setPreview(null)} preview={preview} />}
    </>
  );
};

export default CreateOrUpdateMailTemplate;
